import Btn from 'components/buttons/Btn'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { Form } from 'react-bootstrap'
import { platform } from 'utils'

/**
 * A silly button that triggers a click on a hidden file input. Bootstrap's file input always show the file name, which is not useful when you interact with the file the moment it gets selected.
 * This accepts all usual properties from {@link Btn}, so you can color/size/iconize it the way you want.
 * @param onSelectFile {function} What you'll do with the file
 * @param accept {string[]} List of mimetypes
 * @param capturePics {boolean} If it should allow capturing pictures as well
 * @param children {string|JSX.Element} The label
 * @param btnAttr {object}
 * @returns {JSX.Element}
 */
export default function FileInputBtn({ onSelectFile, accept = [], capturePics = false, children, ...btnAttr }) {
	//Using the attr (instead of accept=capture) would force the button into capture-only on iOS
	//On Cordova Android, this brings a picker to select between camera or file picker; on Android browsers, it brings a picture picker directly, but which allows to select the camera as a source
	const separateAttr = platform.isAndroid()
	if (capturePics && !separateAttr) {
		accept.push('capture')
	}
	const acceptStr = accept.join(';')
	const inputRef = useRef()

	function onClick() {
		inputRef.current.click()
	}

  return <>
	  <Btn onClick={onClick} {...btnAttr}>{children}</Btn>
	  <Form.File ref={inputRef} className="d-none"
	             onChange={onSelectFile} accept={acceptStr} capture={capturePics && separateAttr}/>
  </>
}

FileInputBtn.propTypes = {
	onSelectFile: PropTypes.func.isRequired,
	accept      : PropTypes.arrayOf(PropTypes.string),
	capturePics : PropTypes.bool,
	children    : PropTypes.node,
}

/**
 * Creates a {@link FileInputBtn} which includes a camera icon, automatically allows capturing pictures with the device camera, and filters by image types.
 * If either the jpg/gif/png attributes are true, will only allow those extensions; if none is given, will allow any image type.
 * @param onSelectFile
 * @param children
 * @param jpg {boolean}
 * @param gif {boolean}
 * @param png {boolean}
 * @param btnAttr {object}
 * @returns {JSX.Element}
 */
FileInputBtn.Pic = function ({ onSelectFile, children, jpg = false, gif = false, png = false, ...btnAttr }) {
	const capturePics = platform.isNative()

	let accept = []
	const allowedTypes = { jpeg: jpg, gif, png }
	for (let type in allowedTypes) {
		if (allowedTypes[type]) {
			accept.push(`image/${type}`)
		}
	}
	if (!accept.length) {
		accept = ['image/*']
	}

	return <FileInputBtn {...{ accept, capturePics, children, onSelectFile }} {...btnAttr} swap icon="camera"/>
}

FileInputBtn.Pic.propTypes = {
	onSelectFile: PropTypes.func.isRequired,
	jpg         : PropTypes.bool,
	png         : PropTypes.bool,
	gif         : PropTypes.bool,
	children    : PropTypes.node,
}
